import { useEffect, useState } from 'react'

// Decides whether the bottom tab bar should be visible. Combines two
// independent signals the owner asked for:
//   A) keyboard auto-hide  — hide while the on-screen keyboard is up
//      (input/textarea/contenteditable focused, or visualViewport
//      shrunk vs window.innerHeight).
//   B) scroll-direction hide — hide on a meaningful downward scroll
//      so the user gets max reading area on long surfaces, re-show
//      on any upward scroll, and always show near the top.
//
// We track the two signals separately and AND them together on
// return (visible iff keyboard closed AND scroll-say-show). An
// earlier draft tried to fold them into a single `visible` setter
// and ended up always re-showing the bar on keyboard close even when
// the user was scrolled deep into a list.
export function useBottomBarVisibility() {
  const [keyboardOpen, setKeyboardOpen] = useState(false)
  const [scrollHide, setScrollHide] = useState(false)

  // (A) Keyboard detection. Two signals OR'd into keyboardOpen:
  //   - focus on input/textarea/contenteditable
  //   - visualViewport.height shrunk vs window.innerHeight by > 100px
  //     (the 100px buffer absorbs iOS browser-chrome animation noise
  //     — the viewport jitters that much while the URL bar collapses,
  //     and we don't want that to look like a keyboard event).
  useEffect(() => {
    if (typeof document === 'undefined') return undefined

    let focused = false
    let shrunk = false
    const update = () => setKeyboardOpen(focused || shrunk)

    const isEditable = (el) => {
      if (!el) return false
      const tag = (el.tagName || '').toLowerCase()
      return tag === 'input' || tag === 'textarea' || el.isContentEditable
    }
    const onFocusIn = (e) => {
      if (isEditable(e?.target)) {
        focused = true
        update()
      }
    }
    const onFocusOut = () => {
      // Microtask delay: a focus chain (one input → the next) briefly
      // sees activeElement land on <body> before re-arriving on the
      // next input. Without the delay the bar would flicker.
      setTimeout(() => {
        focused = isEditable(document.activeElement)
        update()
      }, 50)
    }

    const vv = window.visualViewport
    const onViewportResize = () => {
      if (!vv) return
      shrunk = vv.height < window.innerHeight - 100
      update()
    }

    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('focusout', onFocusOut)
    if (vv) {
      vv.addEventListener('resize', onViewportResize)
      onViewportResize()
    }
    return () => {
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('focusout', onFocusOut)
      if (vv) vv.removeEventListener('resize', onViewportResize)
    }
  }, [])

  // (B) Scroll-direction hide. Locked to the app's main scroll
  // container (data-shell-scroll, with the legacy class as fallback).
  // Pages with their own inner scrolling scroll this same parent
  // because of how the layout flexes, so one listener catches
  // everything.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined

    let scroller = null
    let lastY = 0
    let raf = 0
    let pending = false

    const findScroller = () => (
      document.querySelector('main[data-shell-scroll]')
      || document.querySelector('.flex-1.overflow-auto')
    )

    const onScroll = () => {
      if (!scroller) return
      const y = scroller.scrollTop
      const delta = y - lastY
      // Always show near the top — gives the user a stable anchor
      // whenever they pull back to the top of any list.
      if (y < 64) {
        lastY = y
        setScrollHide(false)
        return
      }
      // Hysteresis: only commit on meaningful deltas. The two numbers
      // bias toward showing — a tiny upward scroll already re-shows,
      // but a tiny downward scroll doesn't hide.
      if (delta > 12) {
        lastY = y
        setScrollHide(true)
      } else if (delta < -6) {
        lastY = y
        setScrollHide(false)
      }
    }

    const tick = () => {
      pending = false
      onScroll()
    }
    const handler = () => {
      if (pending) return
      pending = true
      raf = requestAnimationFrame(tick)
    }

    const attach = () => {
      const next = findScroller()
      if (next === scroller) return
      if (scroller) scroller.removeEventListener('scroll', handler)
      scroller = next
      if (scroller) {
        lastY = scroller.scrollTop
        scroller.addEventListener('scroll', handler, { passive: true })
      }
    }

    // Re-attach across navigation. SPA route changes can mount a
    // different scroller; popstate + pageshow + a coarse interval
    // (covers programmatic pushState) all retry the attachment.
    attach()
    window.addEventListener('popstate', attach)
    window.addEventListener('pageshow', attach)
    const interval = setInterval(attach, 1500)

    return () => {
      if (raf) cancelAnimationFrame(raf)
      if (scroller) scroller.removeEventListener('scroll', handler)
      window.removeEventListener('popstate', attach)
      window.removeEventListener('pageshow', attach)
      clearInterval(interval)
    }
  }, [])

  return !keyboardOpen && !scrollHide
}
